import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import BackButton from '../../components/ui/BackButton';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { drawingService } from '../../services/drawingService';
import { snagService } from '../../services/snagService';
import { workerService } from '../../services/workerService';
import { companyService } from '../../services/companyService';
import { useAuth } from '../../contexts/AuthContext';

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
];

const PRIORITY_COLORS = {
  low: '#22c55e',
  medium: '#f59e0b',
  high: '#ef4444',
  critical: '#7c3aed',
};

const STATUS_BADGE = {
  open: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-amber-100 text-amber-700',
  completed: 'bg-green-100 text-green-700',
  resolved: 'bg-green-100 text-green-700',
};

const DrawingViewer = () => {
  const { drawingId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userProfile } = useAuth();

  const imageRef = useRef(null);
  const containerRef = useRef(null);

  const [drawing, setDrawing] = useState(null);
  const [drawingUrl, setDrawingUrl] = useState(null);
  const [snags, setSnags] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [companyId, setCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isPdf, setIsPdf] = useState(false);

  // Marker placement state
  const [placingMarker, setPlacingMarker] = useState(false);
  const [pendingPosition, setPendingPosition] = useState(null);

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedSnag, setSelectedSnag] = useState(null);
  const [showDetailPanel, setShowDetailPanel] = useState(false);

  // Form state
  const [form, setForm] = useState({
    title: '',
    description: '',
    priority: 'medium',
    assignedTo: '',
    dueDate: '',
  });
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const photoInputRef = useRef(null);

  // Status update state
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    loadData();
  }, [drawingId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const companyResult = await companyService?.getUserCompany();
      if (companyResult?.data) setCompanyId(companyResult?.data?.id);

      // Get drawing info from site_drawings table
      const { data: drawingData, error: drawingError } = await supabase_getDrawing(drawingId);
      if (drawingError) throw new Error(drawingError?.message || 'Drawing not found');
      setDrawing(drawingData);
      setIsPdf(drawingData?.mime_type?.includes('pdf'));

      // Get signed URL
      const urlResult = await drawingService?.getDrawingFileUrl(drawingData?.file_path);
      if (urlResult?.url) setDrawingUrl(urlResult?.url);

      // Load snags for this drawing
      const snagsResult = await snagService?.getSnagsByDrawing(drawingId);
      if (snagsResult?.data) setSnags(snagsResult?.data);

      // Load workers for assignment
      const workersResult = await workerService?.getAllWorkers();
      if (workersResult?.data) setWorkers(workersResult?.data);
    } catch (err) {
      console.error('[DrawingViewer] load drawing failed:', err);
      setError('Failed to load the drawing. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  // Inline supabase call for single drawing
  const supabase_getDrawing = async (id) => {
    const { supabase } = await import('../../lib/supabase');
    const { data, error } = await supabase
      ?.from('site_drawings')
      ?.select('*')
      ?.eq('id', id)
      ?.single();
    return { data, error };
  };

  const getRelativePosition = useCallback((e) => {
    const img = imageRef?.current;
    if (!img) return null;
    const rect = img?.getBoundingClientRect();
    const x = ((e?.clientX - rect?.left) / rect?.width);
    const y = ((e?.clientY - rect?.top) / rect?.height);
    return {
      x: Math.max(0, Math.min(1, x)),
      y: Math.max(0, Math.min(1, y)),
    };
  }, []);

  const handleImageClick = useCallback((e) => {
    if (!placingMarker) return;
    const pos = getRelativePosition(e);
    if (!pos) return;
    setPendingPosition(pos);
    setShowCreateModal(true);
    setPlacingMarker(false);
  }, [placingMarker, getRelativePosition]);

  const handleMarkerClick = (e, snag) => {
    e?.stopPropagation();
    setSelectedSnag(snag);
    setShowDetailPanel(true);
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setFormError('');
  };

  const handlePhotoSelect = (files) => {
    const imageFiles = Array.from(files)?.filter(f => f?.type?.startsWith('image/'));
    setPendingPhotos(prev => [...prev, ...imageFiles]);
  };

  const handleCreateSnag = async (e) => {
    e?.preventDefault();
    if (!form?.title?.trim()) { setFormError('Title is required'); return; }
    if (!form?.description?.trim()) { setFormError('Description is required'); return; }

    setSubmitting(true);
    setFormError('');

    try {
      let photoPaths = [];
      if (pendingPhotos?.length > 0) {
        for (const file of pendingPhotos) {
          const { filePath, error: uploadError } = await snagService?.uploadSnagPhoto(
            file, companyId, drawing?.site_id
          );
          if (!uploadError && filePath) photoPaths?.push(filePath);
        }
      }

      const result = await snagService?.createSnag({
        companyId,
        siteId: drawing?.site_id,
        drawingId,
        xPosition: pendingPosition?.x,
        yPosition: pendingPosition?.y,
        title: form?.title?.trim(),
        description: form?.description?.trim(),
        priority: form?.priority,
        assignedTo: form?.assignedTo || null,
        dueDate: form?.dueDate || null,
        photoPaths,
        status: 'open',
      });

      if (result?.error) {
        const e = result.error;
        if (e?.code === 'PGRST301' || e?.code === '42501') {
          setFormError(`Permission denied: ${e?.message ?? 'unknown'}`);
        } else {
          setFormError(`Failed to create snag: ${e?.message ?? 'unknown error'}`);
        }
      } else {
        setSnags(prev => [result?.data, ...prev]);
        setShowCreateModal(false);
        setPendingPosition(null);
        setForm({ title: '', description: '', priority: 'medium', assignedTo: '', dueDate: '' });
        setPendingPhotos([]);
      }
    } catch (err) {
      console.error('[DrawingViewer] create snag failed:', { code: err?.code, message: err?.message });
      if (err?.code === 'PGRST301' || err?.code === '42501') {
        setFormError(`Permission denied: ${err?.message ?? 'unknown'}`);
      } else {
        setFormError(`Failed to create snag: ${err?.message ?? 'unknown error'}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusUpdate = async (snagId, newStatus) => {
    setUpdatingStatus(true);
    try {
      const result = await snagService?.updateSnagStatus(snagId, newStatus);
      if (!result?.error) {
        setSnags(prev => prev?.map(s => s?.id === snagId ? { ...s, status: newStatus } : s));
        if (selectedSnag?.id === snagId) {
          setSelectedSnag(prev => ({ ...prev, status: newStatus }));
        }
      }
    } catch (err) {
      console.error('Status update error:', err);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const workerOptions = [
    { value: '', label: 'Unassigned' },
    ...workers?.filter(w => w?.role === 'worker' || w?.role === 'site_manager')?.map(w => ({
      value: w?.id,
      label: w?.full_name || w?.email,
    }))
  ];

  const canCreate = ['admin', 'site_manager']?.includes(userProfile?.role);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
        </div>
      </div>
    );
  }

  if (error || !drawing) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex flex-col items-center justify-center h-[calc(100vh-80px)] gap-4">
          <Icon name="AlertCircle" size={48} className="text-error" />
          <p className="text-foreground font-medium">{error || 'Drawing not found'}</p>
          <Button variant="outline" onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{drawing?.drawing_name || 'Drawing Viewer'} - SiteProof</title>
      </Helmet>
      <div className="min-h-screen bg-background flex flex-col">
        <Header />

        {/* Toolbar */}
        <div className="pt-16 border-b border-border bg-card">
          <div className="max-w-full px-4 lg:px-6 py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <BackButton fallback="/drawings-plans" />
              <div className="h-4 w-px bg-border flex-shrink-0" />
              <div className="min-w-0">
                <h1 className="text-base font-semibold text-foreground truncate">{drawing?.drawing_name}</h1>
                <p className="text-xs text-muted-foreground">
                  {drawing?.drawing_number && `${drawing?.drawing_number} · `}
                  {drawing?.revision && `Rev ${drawing?.revision} · `}
                  {snags?.length} snag{snags?.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              {canCreate && (
                <Button
                  variant={placingMarker ? 'default' : 'outline'}
                  size="sm"
                  iconName="MapPin"
                  onClick={() => setPlacingMarker(prev => !prev)}
                >
                  {placingMarker ? 'Click to Place' : 'Add Snag'}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Drawing canvas */}
          <div
            ref={containerRef}
            className={`flex-1 overflow-auto bg-muted/30 flex items-start justify-center p-4 ${
              placingMarker ? 'cursor-crosshair' : 'cursor-default'
            }`}
          >
            {placingMarker && (
              <div className="fixed top-24 left-1/2 -translate-x-1/2 z-30 bg-accent text-white text-sm px-4 py-2 rounded-full shadow-lg pointer-events-none">
                <Icon name="MapPin" size={14} className="inline mr-1.5" />
                Click anywhere on the drawing to place a snag marker
              </div>
            )}

            <div className="relative inline-block">
              {isPdf ? (
                <iframe
                  src={drawingUrl}
                  className="w-full min-w-[800px] h-[80vh] border border-border rounded-lg"
                  title={drawing?.drawing_name}
                />
              ) : (
                <img
                  ref={imageRef}
                  src={drawingUrl}
                  alt={drawing?.drawing_name}
                  onClick={handleImageClick}
                  className="max-w-full rounded-lg border border-border shadow-elevation-2 block"
                  style={{ maxHeight: '80vh', objectFit: 'contain' }}
                  draggable={false}
                />
              )}

              {/* Snag markers */}
              {!isPdf && snags?.map(snag => (
                snag?.x_position != null && snag?.y_position != null && (
                  <button
                    key={snag?.id}
                    onClick={(e) => handleMarkerClick(e, snag)}
                    className="absolute transform -translate-x-1/2 -translate-y-full group z-10"
                    style={{
                      left: `${snag?.x_position * 100}%`,
                      top: `${snag?.y_position * 100}%`,
                    }}
                    title={snag?.title}
                  >
                    <div
                      className="w-7 h-7 rounded-full border-2 border-white shadow-lg flex items-center justify-center transition-transform group-hover:scale-125"
                      style={{ backgroundColor: PRIORITY_COLORS?.[snag?.priority] || '#f59e0b' }}
                    >
                      <Icon name="AlertCircle" size={14} color="white" />
                    </div>
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block bg-foreground text-background text-xs px-2 py-1 rounded whitespace-nowrap pointer-events-none">
                      {snag?.title}
                    </div>
                  </button>
                )
              ))}

              {/* Pending marker preview */}
              {pendingPosition && showCreateModal && (
                <div
                  className="absolute transform -translate-x-1/2 -translate-y-full z-20 pointer-events-none"
                  style={{
                    left: `${pendingPosition?.x * 100}%`,
                    top: `${pendingPosition?.y * 100}%`,
                  }}
                >
                  <div className="w-7 h-7 rounded-full border-2 border-white bg-accent shadow-lg flex items-center justify-center animate-pulse">
                    <Icon name="Plus" size={14} color="white" />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Snag list sidebar */}
          <div className="w-72 border-l border-border bg-card flex flex-col overflow-hidden hidden lg:flex">
            <div className="p-4 border-b border-border">
              <h2 className="text-sm font-semibold text-foreground">Snags on this Drawing</h2>
              <p className="text-xs text-muted-foreground mt-0.5">{snags?.length} total</p>
            </div>
            <div className="flex-1 overflow-y-auto">
              {snags?.length === 0 ? (
                <div className="p-6 text-center">
                  <Icon name="MapPin" size={32} className="text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No snags yet</p>
                  {canCreate && (
                    <p className="text-xs text-muted-foreground mt-1">Click "Add Snag" to place a marker</p>
                  )}
                </div>
              ) : (
                snags?.map(snag => (
                  <button
                    key={snag?.id}
                    onClick={() => { setSelectedSnag(snag); setShowDetailPanel(true); }}
                    className={`w-full text-left p-3 border-b border-border hover:bg-muted/50 transition-colors ${
                      selectedSnag?.id === snag?.id ? 'bg-accent/5 border-l-2 border-l-accent' : ''
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <div
                        className="w-3 h-3 rounded-full mt-1 flex-shrink-0"
                        style={{ backgroundColor: PRIORITY_COLORS?.[snag?.priority] || '#f59e0b' }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{snag?.title}</p>
                        <span className={`inline-block mt-1 px-1.5 py-0.5 rounded text-xs font-medium ${STATUS_BADGE?.[snag?.status] || 'bg-muted text-muted-foreground'}`}>
                          {snag?.status === 'in_progress' ? 'In Progress' : snag?.status?.charAt(0)?.toUpperCase() + snag?.status?.slice(1)}
                        </span>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create Snag Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-card rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-border">
              <div>
                <h2 className="text-lg font-semibold text-foreground">Create Snag</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Position: {Math.round((pendingPosition?.x || 0) * 100)}%, {Math.round((pendingPosition?.y || 0) * 100)}%
                </p>
              </div>
              <button
                onClick={() => { setShowCreateModal(false); setPendingPosition(null); }}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <form onSubmit={handleCreateSnag} className="p-5 space-y-4">
              {formError && (
                <div className="flex items-center gap-2 p-3 bg-error/10 border border-error/20 rounded-lg text-sm text-error">
                  <Icon name="AlertCircle" size={16} />
                  {formError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Title <span className="text-error">*</span>
                </label>
                <Input
                  value={form?.title}
                  onChange={e => handleFormChange('title', e?.target?.value)}
                  placeholder="Brief description of the defect"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Description <span className="text-error">*</span>
                </label>
                <textarea
                  value={form?.description}
                  onChange={e => handleFormChange('description', e?.target?.value)}
                  placeholder="Detailed description of the issue..."
                  rows={3}
                  className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent resize-none"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Priority</label>
                  <Select
                    options={PRIORITY_OPTIONS}
                    value={form?.priority}
                    onChange={val => handleFormChange('priority', val)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Due Date</label>
                  <Input
                    type="date"
                    value={form?.dueDate}
                    onChange={e => handleFormChange('dueDate', e?.target?.value)}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Assign Worker</label>
                <Select
                  options={workerOptions}
                  value={form?.assignedTo}
                  onChange={val => handleFormChange('assignedTo', val)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Photos</label>
                <div
                  onClick={() => photoInputRef?.current?.click()}
                  className="border-2 border-dashed border-border rounded-lg p-4 text-center cursor-pointer hover:border-accent/50 hover:bg-muted/30 transition-colors"
                >
                  <Icon name="Camera" size={20} className="text-muted-foreground mx-auto mb-1" />
                  <p className="text-xs text-muted-foreground">Click to add photos</p>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={e => handlePhotoSelect(e?.target?.files)}
                  />
                </div>
                {pendingPhotos?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {pendingPhotos?.map((file, idx) => (
                      <div key={idx} className="relative">
                        <img
                          src={URL.createObjectURL(file)}
                          alt={`Photo ${idx + 1}`}
                          className="w-16 h-16 object-cover rounded-lg border border-border"
                        />
                        <button
                          type="button"
                          onClick={() => setPendingPhotos(prev => prev?.filter((_, i) => i !== idx))}
                          className="absolute -top-1 -right-1 w-5 h-5 bg-error text-white rounded-full flex items-center justify-center"
                        >
                          <Icon name="X" size={10} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-2 border-t border-border">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setShowCreateModal(false); setPendingPosition(null); }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  iconName={submitting ? 'Loader2' : 'MapPin'}
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : 'Save Snag'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Snag Detail Panel */}
      {showDetailPanel && selectedSnag && (
        <div className="fixed inset-y-0 right-0 z-50 w-full max-w-md bg-card border-l border-border shadow-2xl flex flex-col">
          <div className="flex items-center justify-between p-5 border-b border-border">
            <h2 className="text-base font-semibold text-foreground">Snag Details</h2>
            <button
              onClick={() => { setShowDetailPanel(false); setSelectedSnag(null); }}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-5 space-y-5">
            {/* Priority dot + title */}
            <div className="flex items-start gap-3">
              <div
                className="w-4 h-4 rounded-full mt-1 flex-shrink-0"
                style={{ backgroundColor: PRIORITY_COLORS?.[selectedSnag?.priority] || '#f59e0b' }}
              />
              <div>
                <h3 className="text-lg font-semibold text-foreground">{selectedSnag?.title}</h3>
                <p className="text-sm text-muted-foreground mt-0.5">{selectedSnag?.description}</p>
              </div>
            </div>

            {/* Status */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Status</p>
                <span className={`inline-block px-2 py-1 rounded-md text-xs font-medium ${STATUS_BADGE?.[selectedSnag?.status] || 'bg-muted text-muted-foreground'}`}>
                  {selectedSnag?.status === 'in_progress' ? 'In Progress' : selectedSnag?.status?.charAt(0)?.toUpperCase() + selectedSnag?.status?.slice(1)}
                </span>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Priority</p>
                <span className="text-sm font-medium text-foreground capitalize">{selectedSnag?.priority}</span>
              </div>
            </div>

            {selectedSnag?.assigned_user?.full_name && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Assigned To</p>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-accent/20 flex items-center justify-center">
                    <Icon name="User" size={12} color="var(--color-accent)" />
                  </div>
                  <span className="text-sm text-foreground">{selectedSnag?.assigned_user?.full_name}</span>
                </div>
              </div>
            )}

            {selectedSnag?.due_date && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Due Date</p>
                <p className="text-sm text-foreground">
                  {new Date(selectedSnag?.due_date)?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                </p>
              </div>
            )}

            <div>
              <p className="text-xs text-muted-foreground mb-1">Position on Drawing</p>
              <p className="text-sm text-foreground">
                X: {Math.round((selectedSnag?.x_position || 0) * 100)}%, Y: {Math.round((selectedSnag?.y_position || 0) * 100)}%
              </p>
            </div>

            <div>
              <p className="text-xs text-muted-foreground mb-1">Created</p>
              <p className="text-sm text-foreground">
                {new Date(selectedSnag?.created_at)?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
              </p>
            </div>

            {/* Update Status */}
            {canCreate && (
              <div className="pt-4 border-t border-border">
                <p className="text-sm font-medium text-foreground mb-3">Update Status</p>
                <div className="flex flex-col gap-2">
                  {STATUS_OPTIONS?.map(opt => (
                    <button
                      key={opt?.value}
                      onClick={() => handleStatusUpdate(selectedSnag?.id, opt?.value)}
                      disabled={updatingStatus || selectedSnag?.status === opt?.value}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                        selectedSnag?.status === opt?.value
                          ? 'bg-accent/10 border-accent text-accent' :'border-border hover:bg-muted/50 text-foreground'
                      } disabled:opacity-50`}
                    >
                      <Icon
                        name={opt?.value === 'open' ? 'Circle' : opt?.value === 'in_progress' ? 'Clock' : 'CheckCircle'}
                        size={14}
                      />
                      {opt?.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Navigate to full snag detail */}
            <div className="pt-2">
              <Button
                variant="outline"
                fullWidth
                iconName="ExternalLink"
                onClick={() => navigate(`/snags/${selectedSnag?.id}`)}
              >
                View Full Details
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DrawingViewer;
